<template>
  <OnboardingWizardShell
    :total-steps="totalSteps"
    :current-step="currentIndex"
    :show-back="resolvedShowBack"
    :back-label="backLabel"
    :user-first-name="userFirstName"
    :user-last-name="userLastName"
    :user-picture="userPicture"
    :user-name="userName"
    :user-email="userEmail"
    :show-media="resolvedShowMedia"
    :media-label="isProfilePage ? 'Profile badge preview' : 'Step video'"
    :video-embed-url="resolvedVideoEmbedUrl"
    :video-title="resolvedVideoTitle"
    :video-caption="resolvedVideoCaption"
    @back="$emit('back', { index: currentIndex, page: currentPage })"
  >
    <component
      :is="stepComponent"
      v-bind="pageProps"
      @continue="onContinue"
      @skip="onSkip"
      @do-not-ask="onDoNotAsk"
      @replace-photo="onReplacePhoto"
      @remove-photo="onRemovePhoto"
      @preview-change="onPreviewChange"
    />

    <template v-if="isProfilePage" #media>
      <WelcomeWizardMediaPanel
        content="profile"
        :preview-first-name="preview.firstName"
        :preview-last-name="preview.lastName"
        :preview-photo="preview.photoUrl"
      />
    </template>
  </OnboardingWizardShell>
</template>

<script setup>
import { computed, ref } from "vue";
import OnboardingWizardShell from "@components/OnboardingWizardShell/OnboardingWizardShell.vue";
import WelcomeWizardMediaPanel from "@components/WelcomeWizardMediaPanel/WelcomeWizardMediaPanel.vue";
import WelcomeStep from "@components/WelcomeStep/WelcomeStep.vue";
import ProfileStep from "@components/ProfileStep/ProfileStep.vue";
import ReadinessBadgesStep from "@components/ReadinessBadgesStep/ReadinessBadgesStep.vue";
import BrandingStep from "@components/BrandingStep/BrandingStep.vue";
import IqAgentStep from "@components/IqAgentStep/IqAgentStep.vue";
import WebsiteStep from "@components/WebsiteStep/WebsiteStep.vue";
import ManageOrganizationStep from "@components/ManageOrganizationStep/ManageOrganizationStep.vue";

/**
 * ER-12245 — OnboardingWizard (MDP-621).
 *
 * The data-driven onboarding-wizard container. ER-12241 owns the flow: the parent
 * hands this component the API's page list, the 0-based index to show and the
 * landing path, and it renders exactly one page (`pages[currentIndex]`) inside the
 * shared shell, with the stepper segment count taken from `pages.length`. It holds
 * NO flow logic of its own — it never decides the page order, which pages are
 * pending/skipped, or where to land; it only renders the list it is given and
 * emits the user's intent (continue / skip / do-not-ask / photo actions) so the
 * parent can advance and persist (ER-12243).
 */
const props = defineProps({
  /** The API's page list; each page: `{ type, ...stepProps, video*? }`. */
  pages: {
    type: Array,
    default: () => [],
  },
  /** 0-based index of the page currently shown. */
  currentIndex: {
    type: Number,
    default: 0,
  },
  /** Where to land after the final page ("Your Stuff" / "Organization"). */
  landingPath: {
    type: String,
    default: "",
  },
  /** Back button label. */
  backLabel: {
    type: String,
    default: "Back",
  },
  /**
   * Override Back visibility. `null` keeps the default (hidden on the first
   * page); the flow controller passes its own answer so Back is never offered
   * when every page behind the user is already completed.
   */
  showBack: {
    type: Boolean,
    default: null,
  },
  /** Signed-in user identity (top bar). */
  userFirstName: { type: String, default: "" },
  userLastName: { type: String, default: "" },
  userPicture: { type: String, default: "" },
  userName: { type: String, default: "" },
  /** Signed-in email shown in the top bar. */
  userEmail: { type: String, default: "" },
  /** Default right-hand media (overridable per page). */
  showMedia: { type: Boolean, default: true },
  videoEmbedUrl: { type: String, default: "" },
  videoTitle: { type: String, default: "" },
  videoCaption: { type: String, default: "" },
});

const emit = defineEmits([
  "back",
  "continue",
  "complete",
  "skip",
  "do-not-ask",
  "replace-photo",
  "remove-photo",
]);

const PAGE_COMPONENTS = {
  welcome: WelcomeStep,
  profile: ProfileStep,
  readiness: ReadinessBadgesStep,
  branding: BrandingStep,
  iq_agent: IqAgentStep,
  website: WebsiteStep,
  manage_organization: ManageOrganizationStep,
};

const totalSteps = computed(() => Math.max(1, props.pages.length));

/** Back shows by default from page 2 on, unless the parent decides otherwise. */
const resolvedShowBack = computed(() =>
  props.showBack === null ? props.currentIndex > 0 : props.showBack
);

const currentPage = computed(() => props.pages[props.currentIndex] || { type: "" });

const stepComponent = computed(() => PAGE_COMPONENTS[currentPage.value.type] || null);

/** The page's own props minus `type` and the media fields we hoist to the shell. */
const HOISTED_KEYS = new Set(["type", "videoEmbedUrl", "videoTitle", "videoCaption", "showMedia"]);
const pageProps = computed(() => {
  const page = currentPage.value || {};
  return Object.keys(page).reduce((propsForStep, key) => {
    if (!HOISTED_KEYS.has(key)) {
      propsForStep[key] = page[key];
    }
    return propsForStep;
  }, {});
});

const resolvedShowMedia = computed(() => {
  if (currentPage.value && typeof currentPage.value.showMedia === "boolean") {
    return currentPage.value.showMedia;
  }
  return props.showMedia;
});
const resolvedVideoEmbedUrl = computed(
  () => currentPage.value?.videoEmbedUrl ?? props.videoEmbedUrl
);
const resolvedVideoTitle = computed(() => currentPage.value?.videoTitle ?? props.videoTitle);
const resolvedVideoCaption = computed(() => currentPage.value?.videoCaption ?? props.videoCaption);

const isProfilePage = computed(() => currentPage.value.type === "profile");

/** Live name and photo from the profile step, drawn in the shell's media slot. */
const preview = ref({ firstName: "", lastName: "", photoUrl: "" });

function onPreviewChange(values) {
  preview.value = { ...values };
}

const isLastStep = computed(() => props.currentIndex >= props.pages.length - 1);

function onContinue(payload) {
  const event = { index: props.currentIndex, page: currentPage.value, payload };
  emit("continue", event);
  if (isLastStep.value) {
    emit("complete", { landingPath: props.landingPath });
  }
}

function onSkip() {
  emit("skip", { index: props.currentIndex, page: currentPage.value });
}

function onDoNotAsk() {
  emit("do-not-ask", { index: props.currentIndex, page: currentPage.value });
}

function onReplacePhoto(payload) {
  emit("replace-photo", { index: props.currentIndex, page: currentPage.value, payload });
}

function onRemovePhoto() {
  emit("remove-photo", { index: props.currentIndex, page: currentPage.value });
}
</script>
